export type AuthNavigationTarget =
  | { name: 'VerifyEmailRequired' }
  | { name: 'Onboarding' }
  | { name: 'MainTabs'; params: { section: 'home' } }
  | { name: 'Login' };

export type AuthNavigationAction = 'authenticated' | 'logout' | null;

/** Whether auth state just changed in a way that requires navigation. */
export function getAuthNavigationAction(
  wasAuthenticated: boolean | null,
  isAuthenticated: boolean,
  isAuthLoading: boolean
): AuthNavigationAction {
  if (isAuthLoading || wasAuthenticated === null) {
    return null;
  }
  if (isAuthenticated && !wasAuthenticated) {
    return 'authenticated';
  }
  if (!isAuthenticated && wasAuthenticated) {
    return 'logout';
  }
  return null;
}

/** Target route after a successful sign-in / sign-up. */
export function getPostAuthRoute(params: {
  needsEmailVerification: boolean;
  needsOnboarding: boolean;
}): AuthNavigationTarget {
  if (params.needsEmailVerification) {
    return { name: 'VerifyEmailRequired' };
  }
  if (params.needsOnboarding) {
    return { name: 'Onboarding' };
  }
  return { name: 'MainTabs', params: { section: 'home' } };
}

/** Target route after sign-out. */
export function getPostLogoutRoute(): AuthNavigationTarget {
  return { name: 'Login' };
}

/** Post-auth route from React Query auth cache (after sign-in mutation completes). */
export function getPostAuthRouteFromAuthState(params: {
  user: {
    onboarding_completed?: boolean;
    email_verified?: boolean;
  } | null;
  pendingEmailVerify?: boolean;
}): AuthNavigationTarget {
  const needsEmailVerification =
    params.pendingEmailVerify === true ||
    (!!params.user?.onboarding_completed &&
      params.user.email_verified === false);
  const needsOnboarding = !params.user || !params.user.onboarding_completed;
  return getPostAuthRoute({ needsEmailVerification, needsOnboarding });
}
